import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Layout from './components/Layout';
import { BreadcrumbProvider } from './context/BreadcrumbContext';
import { useAuth } from './context/AuthContext';
import NicknameModal from './components/auth/NicknameModal';
import CookieConsent from './components/CookieConsent';  // GDPR Cookie Consent

const GraphView = React.lazy(() => import('./components/GraphView'));
const Home = React.lazy(() => import('./components/Home'));
const StartHere = React.lazy(() => import('./components/StartHere'));
const Topics = React.lazy(() => import('./components/Topics'));
const TopicDetail = React.lazy(() => import('./components/TopicDetail'));
const Thoughts = React.lazy(() => import('./components/Thoughts'));
const ThoughtDetail = React.lazy(() => import('./components/ThoughtDetail'));
const Quotes = React.lazy(() => import('./components/Quotes'));
const QuoteDetail = React.lazy(() => import('./components/QuoteDetail'));
const Passages = React.lazy(() => import('./components/Passages'));
const PassageDetail = React.lazy(() => import('./components/PassageDetail'));
const Tags = React.lazy(() => import('./components/Tags'));
const TagDetail = React.lazy(() => import('./components/TagDetail'));
const Login = React.lazy(() => import('./components/auth/Login'));
const PrivacyPolicy = React.lazy(() => import('./components/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./components/TermsOfService'));
const Register = React.lazy(() => import('./components/auth/Register'));
const VerifyEmail = React.lazy(() => import('./components/auth/VerifyEmail'));
const Profile = React.lazy(() => import('./components/auth/Profile'));
const ForgotPassword = React.lazy(() => import('./components/auth/ForgotPassword'));  // Phase 12: Password reset
const ResetPassword = React.lazy(() => import('./components/auth/ResetPassword'));  // Phase 12: Password reset
const PrivateRoute = React.lazy(() => import('./components/auth/PrivateRoute'));
const Favorites = React.lazy(() => import('./components/Favorites'));  // Member Features
const Notes = React.lazy(() => import('./components/Notes'));  // Member Features
const Preferences = React.lazy(() => import('./pages/Preferences'));  // Notification Preferences
const RandomWisdom = React.lazy(() => import('./components/RandomWisdom'));  // Guest Features Phase 2
const OAuthCallback = React.lazy(() => import('./components/auth/OAuthCallback'));  // OAuth callback handler

function SmallScreenBlocker() {
  const [show, setShow] = React.useState(false);
  React.useEffect(() => {
    const check = () => setShow(window.innerWidth < 375);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  if (!show) return null;
  return (
    <div className="block sm:hidden fixed inset-0 z-[9999] bg-black/90 text-white flex flex-col items-center justify-center">
      <h2 className="text-xl font-bold mb-2">Screen Too Small</h2>
      <p className="text-base">This site requires a device at least 375px wide.</p>
    </div>
  );
}

function TitleUpdater() {
  const location = useLocation();
  useEffect(() => {
    const getPageTitle = () => {
      const path = location.pathname;
      let title = 'Wisdom';
      switch (path) {
        case '/':
          title = 'Wisdom | Home';
          break;
        case '/starthere':
          title = 'Wisdom | Start Here';
          break;
        case '/random-wisdom':  // Guest Features Phase 2
          title = 'Wisdom | Random Wisdom';
          break;
        case '/graph':
          title = 'Wisdom | Graph View';
          break;
        case '/topics':
          title = 'Wisdom | Topics';
          break;
        case '/thoughts':
          title = 'Wisdom | Thoughts';
          break;
        case '/quotes':
          title = 'Wisdom | Quotes';
          break;
        case '/passages':
          title = 'Wisdom | Bible';
          break;
        case '/tags':
          title = 'Wisdom | Tags';
          break;
        case '/login':
          title = 'Wisdom | Login';
          break;
        case '/register':
          title = 'Wisdom | Register';
          break;
        case '/forgot-password':
          title = 'Wisdom | Forgot Password';
          break;
        case '/profile':
          title = 'Wisdom | Profile';
          break;
        case '/favorites':
          title = 'Wisdom | My Favorites';
          break;
        case '/notes':
          title = 'Wisdom | My Notes';
          break;
        case '/preferences':
          title = 'Wisdom | Preferences';
          break;
        case '/privacy-policy':
          title = 'Wisdom | Privacy Policy';
          break;
        case '/terms-of-service':
          title = 'Wisdom | Terms of Service';
          break;
        default:
          if (path.includes('/topics/')) title = 'Wisdom | Topic';
          else if (path.includes('/thoughts/')) title = 'Wisdom | Thought';
          else if (path.includes('/quotes/')) title = 'Wisdom | Quote';
          else if (path.includes('/passages/')) title = 'Wisdom | Passage';
          else if (path.includes('/tags/')) title = 'Wisdom | Tag';
          else if (path.includes('/reset-password/')) title = 'Wisdom | Reset Password';
      }
      return title;
    };
    document.title = getPageTitle();
  }, [location.pathname]);

  return null;
}

function App() {
  const { user, updateNickname } = useAuth();
  const [showNicknameModal, setShowNicknameModal] = useState(false);

  // Phase 3: Check if nickname is required
  useEffect(() => {
    if (user && user.nickname_required) {
      setShowNicknameModal(true);
    } else {
      setShowNicknameModal(false);
    }
  }, [user]);

  const handleNicknameSet = (nickname: string) => {
    updateNickname(nickname);
    setShowNicknameModal(false);
  };

  return (
    <HelmetProvider>
      <Router>
        <TitleUpdater />
        <div className="min-w-[375px]">
          <SmallScreenBlocker />
          {showNicknameModal && <NicknameModal onNicknameSet={handleNicknameSet} />}
          <BreadcrumbProvider>
            <Layout>
              <React.Suspense fallback={
                <div className="flex items-center justify-center min-h-[50vh]">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
                </div>
              }>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<Home />} />
                  <Route path="/starthere" element={<StartHere />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/verify-email/:token" element={<VerifyEmail />} />
                  <Route path="/oauth/callback" element={<OAuthCallback />} />
                  {/* Phase 12: Password Reset Routes */}
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password/:token" element={<ResetPassword />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  {/* Guest Features Phase 2: Random Wisdom Entry Page */}
                  <Route path="/random-wisdom" element={<RandomWisdom />} />
                  {/* Protected Routes - Match Mobile App Structure */}
                  <Route element={<PrivateRoute />}>
                    {/* Profile */}
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/preferences" element={<Preferences />} />
                    <Route path="/favorites" element={<Favorites />} />
                    <Route path="/notes" element={<Notes />} />
                    {/* Collections Lists */}
                    <Route path="/topics" element={<Topics />} />
                    <Route path="/topics/:id" element={<TopicDetail />} />
                    <Route path="/thoughts" element={<Thoughts />} />
                    <Route path="/thoughts/:id" element={<ThoughtDetail />} />
                    <Route path="/quotes" element={<Quotes />} />
                    <Route path="/quotes/:id" element={<QuoteDetail />} />
                    <Route path="/passages" element={<Passages />} />
                    <Route path="/passages/:id" element={<PassageDetail />} />
                    <Route path="/tags" element={<Tags />} />
                    {/* Detail Routes - Match Mobile Pattern */}
                    <Route path="/topic/:id" element={<TopicDetail />} />
                    <Route path="/thought/:id" element={<ThoughtDetail />} />
                    <Route path="/quote/:id" element={<QuoteDetail />} />
                    <Route path="/passage/:id" element={<PassageDetail />} />
                    <Route path="/tags/:tag" element={<TagDetail />} />
                    {/* Legacy/Additional */}
                    <Route path="/graph" element={<GraphView />} />
                  </Route>
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </React.Suspense>
            </Layout>
          </BreadcrumbProvider>

          {/* GDPR Cookie Consent Banner */}
          <CookieConsent />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;
